// ─── La partita e chi l'ha organizzata ───────────────────────────────────────
//
// 🔄 30/09/2026 — **via l'acconto** (decisione del committente: *«la soluzione
// acconto non la useremo più … quindi levala dove appare»*). Questo file era
// `caparra.ts`, e teneva le regole e i contratti dell'acconto della sera, di
// SumUp e del lettore Solo: sono andati via. Restano le cose che nell'acconto
// erano nate ma non sono l'acconto:
//   · le impostazioni degli avvisi all'organizzatore e del campo fisso dal bot
//     (in `app_settings`, sotto la chiave di sempre `caparra`: il nome della
//     chiave nessuno lo legge, e cambiarlo vorrebbe una migrazione per niente);
//   · la conferma della partita chiesta all'organizzatore;
//   · R12 (chi manca lo paga l'organizzatore), il no-show e il suo annullo;
//   · «🔁 Cambia organizzatore»;
//   · «Campo non utilizzabile»;
//   · le trattenute ai maestri già scritte, che si leggono e si tolgono.
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
import { STATI_DELLE_REGOLE, infrazioneSchema } from './disdette.js';
// ── Le impostazioni: gli avvisi all'organizzatore e il campo fisso dal bot ──
/**
 * 🌅 Fra quali ore può stare «Mai prima delle» (26/09/2026). Sotto le 5 è
 * notte per chiunque; sopra mezzogiorno l'avviso del giorno stesso, per una
 * partita del mattino, non arriverebbe più.
 */
export const NON_PRIMA_DELLE_MIN = '05:00';
export const NON_PRIMA_DELLE_MAX = '12:00';
export const avvisiAllOrganizzatoreSchema = z.object({
    /** Il giorno prima, a quest'ora, se la partita è incompleta. */
    vigiliaAlle: oraSchema,
    /** Il giorno stesso, tante ore prima. */
    orePrima: z.number().int().min(1).max(12),
    /**
     * 🔄 26/09/2026 — lo decide il circolo (prima era scritto nel bot, alle 8).
     * 🗣️ *«Voglio deciderlo io … se dobbiamo decidere un fisso sono le 7 di
     * mattina»*. Chi ha salvato le impostazioni prima non lo ha: vale 07:00.
     */
    nonPrimaDelle: oraSchema
        .refine((h) => h >= NON_PRIMA_DELLE_MIN && h <= NON_PRIMA_DELLE_MAX, {
        message: `Fra le ${NON_PRIMA_DELLE_MIN} e le ${NON_PRIMA_DELLE_MAX}`,
    })
        .default('07:00'),
});
/**
 * 🔁 27/09/2026 — **Il campo fisso chiesto al bot** (mockup «Fisso dal bot»,
 * approvato). 🗣️ *«il campo fisso lo farei per due mesi, poi si può sempre
 * cambiare in impostazioni … lo farei creare subito»*. Ogni settimana, alla
 * stessa ora e sullo stesso campo, per al massimo `mesiMax` mesi.
 */
export const fissoDalBotSchema = z.object({
    attivo: z.boolean(),
    mesiMax: z.number().int().min(1).max(6),
});
/** Quello che la Segreteria scrive da Impostazioni › «Avvisi e campo fisso». */
export const modificaImpostazioniPartiteSchema = z.object({
    avvisi: avvisiAllOrganizzatoreSchema,
    fissoDalBot: fissoDalBotSchema,
});
/**
 * Le impostazioni come stanno nel database. Il regolamento (indirizzo e
 * versione) lo scrive la pagina «Documenti del bot» quando lo pubblica: qui si
 * legge e basta. I campi dell'acconto che una riga di prima si porta dietro
 * si ignorano.
 */
export const impostazioniPartiteSchema = z.object({
    avvisi: avvisiAllOrganizzatoreSchema.default({ vigiliaAlle: '18:00', orePrima: 3, nonPrimaDelle: '07:00' }),
    fissoDalBot: fissoDalBotSchema.default({ attivo: true, mesiMax: 2 }),
    regolamento: z
        .object({
        url: z.string().trim().url().startsWith('https://', 'Serve un indirizzo https://').nullable(),
        versione: z.string().trim().min(1).max(40),
    })
        .default({ url: null, versione: '1' }),
});
export const IMPOSTAZIONI_PARTITE_DI_PARTENZA = {
    avvisi: { vigiliaAlle: '18:00', orePrima: 3, nonPrimaDelle: '07:00' },
    fissoDalBot: { attivo: true, mesiMax: 2 },
    regolamento: { url: null, versione: '1' },
};
// ── Le date ─────────────────────────────────────────────────────────────────
const MESI_CON_ANNO = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
/** «25 settembre 2027»: la scadenza di un Credito, che sta sempre più in là. */
export function dataConAnno(data) {
    const [a, m, g] = data.split('-');
    return `${Number(g)} ${MESI_CON_ANNO[Number(m) - 1]} ${a}`;
}
// ── La conferma della partita, chiesta all'organizzatore ───────────────────
/**
 * Gli avvisi all'organizzatore (§ 4.4), e cosa ha risposto. `chiesta` la segna
 * il bot quando manda la domanda, così la Segreteria vede anche chi non
 * risponde.
 */
export const RISPOSTE_ALLA_CONFERMA = ['chiesta', 'confermata', 'problema', 'ci_pensiamo'];
export const rispostaConfermaSchema = z.enum(RISPOSTE_ALLA_CONFERMA);
export const MOMENTI_DELLA_CONFERMA = ['72h', 'vigilia', 'giorno'];
export const confermaDalPonteSchema = z.object({
    memberId: z.uuid(),
    moment: z.enum(MOMENTI_DELLA_CONFERMA),
    answer: rispostaConfermaSchema,
});
export const ETICHETTE_CONFERMA = {
    chiesta: '❔ non ha risposto',
    confermata: '✅ confermata',
    problema: '⚠️ problema',
    ci_pensiamo: 'ci pensa l’organizzatore',
};
// ── Il no-show, R12, e la partita vista dalla Segreteria ───────────────────
/** ↩️ Voce 30 — un no-show annullato dalla Segreteria: chi, quando e perché. */
export const annulloNoShowSchema = z.object({
    at: z.string(),
    by: z.string().nullable(),
    reason: z.string(),
});
/** R12 nella scheda della prenotazione: quanti mancano, e quanto va all'organizzatore. */
export const quotaMancanteSchema = z.object({
    missing: z.number().int(),
    quotaCents: z.number().int(),
    organizerTotalCents: z.number().int(),
    organizerMemberId: z.uuid(),
    organizerName: z.string(),
    done: z.boolean(),
});
/** La partita vista dalla Segreteria: chi manca, il no-show, le regole di chi l'ha organizzata. */
export const organizzatoreDellaPartitaSchema = z.object({
    missing: quotaMancanteSchema.nullable(),
    noShow: z.boolean(),
    /** 🆕 Voce 30 — l'ultimo no-show annullato su questa partita, se adesso non è segnata. */
    noShowCancelled: annulloNoShowSchema.nullable().default(null),
    /**
     * 🚦 Fase B — lo stato di chi l'ha organizzata (libero / obbligo carta, «N su
     * 5») e l'infrazione viva su questa partita. Nullo senza organizzatore.
     */
    rules: z
        .object({
        state: z.enum(STATI_DELLE_REGOLE),
        status: z.enum(['libero', 'obbligo_carta']),
        regularGames: z.number().int(),
        needed: z.number().int(),
        freeUntil: z.object({ date: dataSchema, time: oraSchema }),
    })
        .nullable()
        .default(null),
    infraction: infrazioneSchema.nullable().default(null),
});
export const motivoObbligatorioSchema = z.object({ reason: z.string().trim().min(3).max(500) });
// ── Campo non utilizzabile (R1) ─────────────────────────────────────────────
/** Quali campi, quando, perché. */
export const campoInagibileSchema = z.object({
    courtIds: z.array(z.uuid()).min(1),
    date: dataSchema,
    from: oraSchema,
    to: oraSchema,
    reason: z.string().trim().min(3).max(200),
});
export const prenotazioneColpitaSchema = z.object({
    bookingId: z.uuid(),
    courtName: z.string(),
    time: oraSchema,
    endTime: oraSchema,
    type: z.string(),
    organizer: z.string().nullable(),
});
export const colpiteSchema = z.object({ items: z.array(prenotazioneColpitaSchema) });
// ── Le trattenute ai maestri ────────────────────────────────────────────────
/**
 * Una trattenuta dal compenso del maestro. 🔄 30/09/2026: con l'acconto è andata
 * anche la regola che le scriveva (le lezioni della sera disdette tardi): quelle
 * già scritte si leggono nel conto dei maestri e si tolgono col motivo.
 */
export const trattenutaDelMaestroSchema = z.object({
    id: z.uuid(),
    instructorId: z.uuid(),
    date: dataSchema,
    time: z.string(),
    courtName: z.string(),
    students: z.string(),
    reason: z.enum(['disdetta_tardiva', 'no_show']),
    hoursBefore: z.number().int().nullable(),
    amountCents: z.number().int(),
});
// ── 🔁 Cambia organizzatore ─────────────────────────────────────────────────
export const STATI_CAMBIO = ['pending', 'accepted', 'done', 'declined', 'expired', 'withdrawn'];
export const cambioOrganizzatoreSchema = z.object({
    id: z.uuid(),
    bookingId: z.uuid(),
    fromMemberId: z.uuid(),
    fromName: z.string(),
    toMemberId: z.uuid(),
    toName: z.string(),
    /** Il primo organizzatore lascia il suo posto in campo al nuovo. */
    leavesPlace: z.boolean(),
    /** 🚪 25/10/2026: il primo organizzatore, a cambio fatto, esce dalla partita (il nuovo è già in campo). */
    leavesMatch: z.boolean().default(false),
    via: z.enum(['bot', 'staff']),
    status: z.enum(STATI_CAMBIO),
    expiresAt: z.string(),
    date: dataSchema,
    time: oraSchema,
    courtName: z.string(),
});
/** Dalla Segreteria: chi, e se prende il posto del primo. */
export const cambiaOrganizzatoreSchema = z.object({
    toMemberId: z.uuid(),
    leavesPlace: z.boolean().default(false),
});
/** Dal bot: chi chiede, a chi, e se gli lascia il posto. */
export const chiediCambioSchema = z.object({
    memberId: z.uuid(),
    toMemberId: z.uuid(),
    leavesPlace: z.boolean().default(false),
    /** 🚪 25/10/2026 (bot, mockup «Passa l’organizzazione», A): a un socio già in campo, e chi chiede esce. */
    leavesMatch: z.boolean().default(false),
});
export const rispostaCambioSchema = z.object({
    memberId: z.uuid(),
    accept: z.boolean(),
});
export const esitoCambioSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato', 'non_lo_so']),
    reason: z.string().nullable(),
    /** Il codice del rifiuto, per chi vuole distinguere un «no» dall'altro. */
    code: z.string().nullable().default(null),
    change: cambioOrganizzatoreSchema.nullable(),
});
//# sourceMappingURL=organizzatore.js.map